import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  IconNews,
  IconMessages,
  IconRss,
  IconTags,
  IconTrendingUp,
  IconClock,
  IconUsers,
  IconMessageCircle,
  IconBuildingStore,
  IconClipboardList,
  IconSpeakerphone,
} from '@tabler/icons-react';
import { listNews, countArticles, countArticlesByCategory } from '../services/newsService';
import { listRooms, listTopics, countTopics, countRooms } from '../services/forumService';
import { listSources } from '../services/rssService';
import { listCategories } from '../services/categoriesService';
import { countUsers } from '../services/usersService';
import { countPendingQuotes } from '../services/quotesService';
import { countSuppliers } from '../services/suppliersService';
import { countActiveCampaigns } from '../services/campaignsService';
import { NewsArticle, ForumTopic, ForumRoom, NewsCategory, NewsSource } from '../types';

interface DashboardStats {
  totalArticles: number;
  totalTopics: number;
  totalRooms: number;
  activeSources: number;
  totalCategories: number;
  totalUsers: number;
  totalSuppliers: number;
  pendingQuotes: number;
  activeCampaigns: number;
  totalForumPosts: number;
  recentArticles: NewsArticle[];
  recentTopics: ForumTopic[];
  topCategories: { category: NewsCategory; count: number }[];
}

export default function DashboardPage() {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch data in parallel
      const [
        newsResult,
        topicsResult,
        roomsResult,
        sourcesResult,
        categories,
        totalArticles,
        totalTopics,
        totalRooms,
        totalUsers,
        totalSuppliers,
        pendingQuotes,
        activeCampaigns,
      ] = await Promise.all([
        listNews({ pageSize: 5, sortBy: 'publishedAt', sortDirection: 'desc' }),
        listTopics({ pageSize: 5 }),
        listRooms({ pageSize: 100 }),
        listSources({ pageSize: 100 }),
        listCategories(),
        countArticles(),
        countTopics(),
        countRooms(),
        countUsers(),
        countSuppliers(),
        countPendingQuotes(),
        countActiveCampaigns(),
      ]);

      // Count active sources
      const activeSources = sourcesResult.sources.filter((s: NewsSource) => s.isActive).length;

      // Sum forum posts from denormalized room counters (no extra query needed)
      const totalForumPosts = roomsResult.rooms.reduce(
        (sum: number, r: ForumRoom) => sum + (r.postCount ?? 0),
        0
      );

      // Get per-category article counts via aggregation (no full document fetch)
      const categoryCounts = await Promise.all(
        categories.map((cat: NewsCategory) => countArticlesByCategory(cat.id!))
      );
      const topCategories = categories
        .map((cat: NewsCategory, i: number) => ({ category: cat, count: categoryCounts[i] }))
        .filter((entry: { category: NewsCategory; count: number }) => entry.count > 0)
        .sort(
          (
            a: { category: NewsCategory; count: number },
            b: { category: NewsCategory; count: number }
          ) => b.count - a.count
        )
        .slice(0, 5);

      setStats({
        totalArticles,
        totalTopics,
        totalRooms,
        activeSources,
        totalCategories: categories.length,
        totalUsers,
        totalSuppliers,
        pendingQuotes,
        activeCampaigns,
        totalForumPosts,
        recentArticles: newsResult.articles,
        recentTopics: topicsResult.topics,
        topCategories,
      });
    } catch (err) {
      console.error('Error loading dashboard:', err);
      setError(err instanceof Error ? err.message : 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="page-header d-print-none">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-header d-print-none">
        <div className="alert alert-danger">{error}</div>
      </div>
    );
  }

  if (!stats) return null;

  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  return (
    <>
      {/* Page header */}
      <div className="page-header d-print-none">
        <div className="row g-2 align-items-center">
          <div className="col">
            <div className="page-pretitle">ForestSIM Mobile</div>
            <h2 className="page-title">Dashboard</h2>
          </div>
        </div>
      </div>

      {/* Page body */}
      <div className="page-body">
        {/* Stats Cards — Row 1 */}
        <div className="row row-deck row-cards mb-3">
          <div className="col-sm-6 col-lg-3">
            <div className="card">
              <div className="card-body">
                <div className="d-flex align-items-center">
                  <div className="subheader">Registered Users</div>
                  <div className="ms-auto lh-1">
                    <IconUsers className="icon text-primary" size={24} />
                  </div>
                </div>
                <div className="h1 mb-0">{stats.totalUsers}</div>
                <div className="text-muted mt-2">
                  <Link to="/users" className="text-decoration-none">
                    View all →
                  </Link>
                </div>
              </div>
            </div>
          </div>

          <div className="col-sm-6 col-lg-3">
            <div className="card">
              <div className="card-body">
                <div className="d-flex align-items-center">
                  <div className="subheader">Total Articles</div>
                  <div className="ms-auto lh-1">
                    <IconNews className="icon text-blue" size={24} />
                  </div>
                </div>
                <div className="h1 mb-0">{stats.totalArticles}</div>
                <div className="text-muted mt-2">
                  <Link to="/news" className="text-decoration-none">
                    View all →
                  </Link>
                </div>
              </div>
            </div>
          </div>

          <div className="col-sm-6 col-lg-3">
            <div className="card">
              <div className="card-body">
                <div className="d-flex align-items-center">
                  <div className="subheader">Forum Topics</div>
                  <div className="ms-auto lh-1">
                    <IconMessageCircle className="icon text-success" size={24} />
                  </div>
                </div>
                <div className="h1 mb-0">{stats.totalTopics}</div>
                <div className="text-muted mt-2">
                  <Link to="/forum-topics" className="text-decoration-none">
                    View all →
                  </Link>
                </div>
              </div>
            </div>
          </div>

          <div className="col-sm-6 col-lg-3">
            <div className="card">
              <div className="card-body">
                <div className="d-flex align-items-center">
                  <div className="subheader">Pending Quotes</div>
                  <div className="ms-auto lh-1">
                    <IconClipboardList className="icon text-danger" size={24} />
                  </div>
                </div>
                <div className="h1 mb-0">{stats.pendingQuotes}</div>
                <div className="text-muted mt-2">
                  <Link to="/quotes" className="text-decoration-none">
                    Review →
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Stats Cards — Row 2 */}
        <div className="row row-deck row-cards mb-4">
          <div className="col-sm-6 col-lg-3">
            <div className="card">
              <div className="card-body">
                <div className="d-flex align-items-center">
                  <div className="subheader">Suppliers</div>
                  <div className="ms-auto lh-1">
                    <IconBuildingStore className="icon text-teal" size={24} />
                  </div>
                </div>
                <div className="h1 mb-0">{stats.totalSuppliers}</div>
                <div className="text-muted mt-2">
                  <Link to="/suppliers" className="text-decoration-none">
                    View all →
                  </Link>
                </div>
              </div>
            </div>
          </div>

          <div className="col-sm-6 col-lg-3">
            <div className="card">
              <div className="card-body">
                <div className="d-flex align-items-center">
                  <div className="subheader">Active Campaigns</div>
                  <div className="ms-auto lh-1">
                    <IconSpeakerphone className="icon text-orange" size={24} />
                  </div>
                </div>
                <div className="h1 mb-0">{stats.activeCampaigns}</div>
                <div className="text-muted mt-2">
                  <Link to="/campaigns" className="text-decoration-none">
                    View all →
                  </Link>
                </div>
              </div>
            </div>
          </div>

          <div className="col-sm-6 col-lg-3">
            <div className="card">
              <div className="card-body">
                <div className="d-flex align-items-center">
                  <div className="subheader">RSS Sources</div>
                  <div className="ms-auto lh-1">
                    <IconRss className="icon text-warning" size={24} />
                  </div>
                </div>
                <div className="h1 mb-0">
                  {stats.activeSources}
                  <span className="text-muted fs-5"> active</span>
                </div>
                <div className="text-muted mt-2">
                  <Link to="/rss-sources" className="text-decoration-none">
                    Manage →
                  </Link>
                </div>
              </div>
            </div>
          </div>

          <div className="col-sm-6 col-lg-3">
            <div className="card">
              <div className="card-body">
                <div className="d-flex align-items-center">
                  <div className="subheader">Forum Rooms</div>
                  <div className="ms-auto lh-1">
                    <IconMessages className="icon text-info" size={24} />
                  </div>
                </div>
                <div className="h1 mb-0">{stats.totalRooms}</div>
                <div className="text-muted small mt-1">{stats.totalForumPosts} posts total</div>
                <div className="text-muted mt-2">
                  <Link to="/forum-rooms" className="text-decoration-none">
                    View all →
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content Row */}
        <div className="row row-deck row-cards">
          {/* Recent Articles */}
          <div className="col-lg-6">
            <div className="card">
              <div className="card-header">
                <h3 className="card-title">
                  <IconClock className="icon me-2" size={20} />
                  Recent Articles
                </h3>
              </div>
              <div className="card-body p-0">
                <div className="list-group list-group-flush">
                  {stats.recentArticles.length === 0 ? (
                    <div className="p-3 text-muted">No articles yet</div>
                  ) : (
                    stats.recentArticles.map(article => (
                      <div key={article.id} className="list-group-item">
                        <div className="row align-items-center">
                          <div className="col-auto">
                            {article.imageUrl ? (
                              <img
                                src={article.imageUrl}
                                alt=""
                                className="avatar avatar-sm"
                                style={{ objectFit: 'cover' }}
                              />
                            ) : (
                              <span className="avatar avatar-sm">
                                <IconNews size={20} />
                              </span>
                            )}
                          </div>
                          <div className="col text-truncate">
                            <div className="text-truncate">
                              <strong>{article.title}</strong>
                            </div>
                            <div className="text-muted text-truncate">
                              {article.source} • {formatDate(article.publishedAt)}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
              <div className="card-footer">
                <Link to="/news" className="btn btn-primary w-100">
                  View All Articles
                </Link>
              </div>
            </div>
          </div>

          {/* Recent Forum Topics */}
          <div className="col-lg-6">
            <div className="card">
              <div className="card-header">
                <h3 className="card-title">
                  <IconTrendingUp className="icon me-2" size={20} />
                  Recent Forum Activity
                </h3>
              </div>
              <div className="card-body p-0">
                <div className="list-group list-group-flush">
                  {stats.recentTopics.length === 0 ? (
                    <div className="p-3 text-muted">No topics yet</div>
                  ) : (
                    stats.recentTopics.map(topic => (
                      <div key={topic.id} className="list-group-item">
                        <div className="row align-items-center">
                          <div className="col-auto">
                            <span className="avatar avatar-sm">
                              <IconMessageCircle size={20} />
                            </span>
                          </div>
                          <div className="col text-truncate">
                            <div className="text-truncate">
                              <strong>{topic.title}</strong>
                            </div>
                            <div className="text-muted">
                              <IconUsers className="icon icon-xs me-1" />
                              {topic.postCount || 0} posts • {formatDate(topic.lastActivityAt)}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
              <div className="card-footer">
                <Link to="/forum-topics" className="btn btn-success w-100">
                  View All Topics
                </Link>
              </div>
            </div>
          </div>

          {/* Top Categories */}
          <div className="col-lg-12">
            <div className="card">
              <div className="card-header">
                <h3 className="card-title">
                  <IconTags className="icon me-2" size={20} />
                  Top Categories by Article Count
                </h3>
              </div>
              <div className="card-body">
                {stats.topCategories.length === 0 ? (
                  <div className="text-muted">No categories with articles yet</div>
                ) : (
                  <div className="row g-3">
                    {stats.topCategories.map(entry => (
                      <div key={entry.category.id} className="col-md-4 col-lg-3">
                        <div className="card card-sm">
                          <div className="card-body">
                            <div className="d-flex align-items-center justify-content-between">
                              <div>
                                <div className="fw-bold">{entry.category.name}</div>
                                <div className="text-muted small">{entry.count} articles</div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="card-footer">
                <Link to="/categories" className="btn btn-outline-primary w-100">
                  Manage Categories
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
